import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, XCircle, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface FilamentManagerProps {
  searchQuery: string;
  selectedManufacturer: string;
  selectedType: string;
  selectedStatus: string;
}

interface FilamentData {
  id: number;
  filament_name: string;
  filament_type: string;
  status: string;
  created_at: string;
  manufacturer_id: number;
}

const FilamentManager = ({ searchQuery, selectedManufacturer, selectedType, selectedStatus }: FilamentManagerProps) => {
  const queryClient = useQueryClient();

  const { data: filaments, isLoading } = useQuery({
    queryKey: ['admin-filaments', searchQuery, selectedManufacturer, selectedType, selectedStatus],
    queryFn: async () => {
      let query = supabase
        .from('filaments')
        .select(`
          id,
          filament_name,
          filament_type,
          status,
          created_at,
          manufacturer_id
        `)
        .order('created_at', { ascending: false });

      if (searchQuery) {
        query = query.ilike('filament_name', `%${searchQuery}%`);
      }

      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      if (selectedType !== 'all') {
        query = query.eq('filament_type', selectedType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FilamentData[];
    }
  });

  const approveMutation = useMutation({
    mutationFn: async (filamentId: number) => {
      const { error } = await supabase
        .from('filaments')
        .update({ status: 'approved' })
        .eq('id', filamentId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filaments'] });
      toast.success('Filament approved successfully');
    },
    onError: (error) => {
      console.error('Error approving filament:', error);
      toast.error('Failed to approve filament');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async (filamentId: number) => {
      const { error } = await supabase
        .from('filaments')
        .update({ status: 'rejected' })
        .eq('id', filamentId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filaments'] });
      toast.success('Filament rejected');
    },
    onError: (error) => {
      console.error('Error rejecting filament:', error);
      toast.error('Failed to reject filament');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (filamentId: number) => {
      const { error } = await supabase
        .from('filaments')
        .delete()
        .eq('id', filamentId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-filaments'] });
      toast.success('Filament deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting filament:', error);
      toast.error('Failed to delete filament');
    }
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'approved': 'default',
      'pending': 'secondary',
      'rejected': 'destructive',
      'draft': 'outline'
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading filaments...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Filament Management</CardTitle>
      </CardHeader>
      <CardContent>
        {filaments?.length === 0 ? (
          <p className="text-muted-foreground">No filaments found matching your filters.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filaments?.map((filament) => (
                <TableRow key={filament.id}>
                  <TableCell className="font-medium">{filament.filament_name}</TableCell>
                  <TableCell>{filament.filament_type}</TableCell>
                  <TableCell>{getStatusBadge(filament.status)}</TableCell>
                  <TableCell>
                    {new Date(filament.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>

                      {filament.status === 'pending' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => approveMutation.mutate(filament.id)}
                            disabled={approveMutation.isPending}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => rejectMutation.mutate(filament.id)}
                            disabled={rejectMutation.isPending}
                            className="text-red-600 hover:text-red-700"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Filament</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{filament.filament_name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(filament.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default FilamentManager;
